/**
 * OwnerRewardsPanel - Full rewards management panel for project owners
 *
 * Orchestrates:
 * - No period: "Open Claim Period" button -> OpenPeriodForm
 * - Open period: Live claim count, "Close Claims" button
 * - Closed period: PeriodClaimantsTable with rate config and CSV/distribute actions
 * - Always: Period history at bottom
 *
 * Feature: 017-rewards-claim-system
 */

import React, { useState, useCallback } from 'react';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { TextField } from '../../ui/components/TextField';
import { TextArea } from '../../ui/components/TextArea';
import { FeatherGift, FeatherDownload, FeatherCheckCircle, FeatherAlertCircle, FeatherCopy, FeatherCheck } from '@subframe/core';
import {
  useActivePeriod,
  usePeriods,
  useOpenPeriod,
  useClosePeriod,
  useSetRate,
  useMarkDistributed,
  useClaimants,
  useExportCSV,
} from '../../hooks/useRewards';
import type { ClaimPeriod, OpenPeriodConfig } from '../../api/rewards';

interface OwnerRewardsPanelProps {
  projectSlug: string;
  projectCollections: Array<{ id: string; name: string }>;
}

export const OwnerRewardsPanel: React.FC<OwnerRewardsPanelProps> = ({
  projectSlug,
  projectCollections,
}) => {
  const { data: activeData, isLoading: isLoadingActive } = useActivePeriod(projectSlug);
  const { data: periodsData, isLoading: isLoadingPeriods } = usePeriods(projectSlug);
  const [showForm, setShowForm] = useState(false);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);

  const activePeriod = activeData?.period;
  const periods = periodsData?.periods || [];

  // Find the most recent closed period for management (if no active period)
  const closedPeriod = !activePeriod
    ? periods.find(p => p.status === 'closed')
    : null;

  const managePeriod = activePeriod || closedPeriod;

  if (isLoadingActive || isLoadingPeriods) {
    return <div className="text-subtext-color py-4">Loading rewards data...</div>;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full items-center gap-2">
        <FeatherGift className="h-5 w-5 text-green-600" />
        <span className="text-heading-3 font-heading-3 text-default-font">
          Rewards Management
        </span>
      </div>

      {/* Active/Closed Period Management */}
      {managePeriod ? (
        <PeriodManagement
          projectSlug={projectSlug}
          period={managePeriod}
          projectCollections={projectCollections}
        />
      ) : showForm ? (
        <OpenPeriodForm
          projectSlug={projectSlug}
          projectCollections={projectCollections}
          lastPeriod={periods[0] || null}
          onCancel={() => setShowForm(false)}
          onSuccess={() => setShowForm(false)}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-body font-body text-subtext-color">
            No active claim period. Open one to allow holders to register for rewards.
          </p>
          <Button
            className="w-fit"
            variant="brand-primary"
            onClick={() => setShowForm(true)}
          >
            Open Claim Period
          </Button>
        </div>
      )}

      {/* Period History */}
      {periods.length > 0 && (
        <div className="flex w-full flex-col gap-3 mt-4">
          <span className="text-body-bold font-body-bold text-subtext-color">
            Period History
          </span>
          <div className="flex flex-col gap-2">
            {periods.map(period => (
              <div
                key={period.id}
                className="flex items-center justify-between rounded border border-solid border-neutral-border p-3 cursor-pointer hover:bg-default-background"
                onClick={() => setSelectedPeriodId(selectedPeriodId === period.id ? null : period.id)}
              >
                <div className="flex items-center gap-3">
                  <Badge variant={
                    period.status === 'open' ? 'success' :
                    period.status === 'closed' ? 'warning' : 'neutral'
                  }>
                    {period.status}
                  </Badge>
                  <span className="text-body font-body text-default-font">
                    {new Date(period.opened_at).toLocaleDateString()}
                    {period.closed_at ? ` - ${new Date(period.closed_at).toLocaleDateString()}` : ' - present'}
                  </span>
                </div>
                <span className="text-caption font-caption text-subtext-color">
                  {Number(period.live_claim_count ?? period.total_claims ?? 0).toLocaleString()}
                  {period.total_eligible_wallets ? ` / ${Number(period.total_eligible_wallets).toLocaleString()}` : ''} wallets
                  {period.total_points_claimants != null && (
                    <>, {Number(period.total_points_claimants).toLocaleString()}
                    {period.total_points_all_holders ? ` / ${Number(period.total_points_all_holders).toLocaleString()}` : ''} pts</>
                  )}
                </span>
              </div>
            ))}
          </div>

          {/* Expanded period details */}
          {selectedPeriodId && (
            <PeriodClaimantsTable
              projectSlug={projectSlug}
              periodId={selectedPeriodId}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ---- OpenPeriodForm ----

interface OpenPeriodFormProps {
  projectSlug: string;
  projectCollections: Array<{ id: string; name: string }>;
  lastPeriod: ClaimPeriod | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const OpenPeriodForm: React.FC<OpenPeriodFormProps> = ({
  projectSlug,
  projectCollections,
  lastPeriod,
  onCancel,
  onSuccess,
}) => {
  const openMutation = useOpenPeriod(projectSlug);

  const [selectedCollections, setSelectedCollections] = useState<string[]>(
    lastPeriod?.eligible_collection_ids || projectCollections.map(c => c.id)
  );
  const [rewardAddressType, setRewardAddressType] = useState(
    lastPeriod?.required_reward_address_type || 'coreum'
  );
  const [minThreshold, setMinThreshold] = useState(
    lastPeriod?.min_points_threshold?.toString() || '0'
  );
  const [rateMode, setRateMode] = useState<'per_point' | 'fixed_pool'>(
    (lastPeriod?.rate_mode as 'per_point' | 'fixed_pool') || 'per_point'
  );
  const [ratePerPoint, setRatePerPoint] = useState(
    lastPeriod?.rate_per_point?.toString() || ''
  );
  const [totalPool, setTotalPool] = useState(
    lastPeriod?.total_pool?.toString() || ''
  );
  const [customMessage, setCustomMessage] = useState(lastPeriod?.custom_message || '');

  const toggleCollection = (id: string) => {
    setSelectedCollections(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCollections.length === 0) return;

    const config: OpenPeriodConfig = {
      eligible_collection_ids: selectedCollections,
      required_reward_address_type: rewardAddressType,
      min_points_threshold: parseInt(minThreshold) || 0,
      rate_mode: rateMode,
      ...(rateMode === 'per_point' && ratePerPoint ? { rate_per_point: parseFloat(ratePerPoint) } : {}),
      ...(rateMode === 'fixed_pool' && totalPool ? { total_pool: parseFloat(totalPool) } : {}),
      ...(customMessage.trim() ? { custom_message: customMessage.trim() } : {}),
    };

    openMutation.mutate(config, {
      onSuccess: () => onSuccess(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 rounded border border-solid border-neutral-border p-4">
      <span className="text-body-bold font-body-bold text-default-font">
        Open New Claim Period
      </span>

      {/* Collection selection */}
      <div className="flex flex-col gap-2">
        <span className="text-caption-bold font-caption-bold text-default-font">
          Eligible Collections
        </span>
        {projectCollections.map(col => (
          <label key={col.id} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedCollections.includes(col.id)}
              onChange={() => toggleCollection(col.id)}
              className="rounded"
            />
            <span className="text-body font-body text-default-font">{col.name}</span>
          </label>
        ))}
        {selectedCollections.length === 0 && (
          <span className="text-caption font-caption text-red-600">
            Select at least one collection
          </span>
        )}
      </div>

      {/* Reward address type */}
      <div className="flex flex-col gap-2">
        <span className="text-caption-bold font-caption-bold text-default-font">
          Reward Address Type
        </span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="rewardType"
              value="coreum"
              checked={rewardAddressType === 'coreum'}
              onChange={() => setRewardAddressType('coreum')}
            />
            <span className="text-body font-body text-default-font">Coreum</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="rewardType"
              value="xrp"
              checked={rewardAddressType === 'xrp'}
              onChange={() => setRewardAddressType('xrp')}
            />
            <span className="text-body font-body text-default-font">XRP</span>
          </label>
        </div>
      </div>

      {/* Min threshold */}
      <TextField
        className="h-auto w-full max-w-[300px] flex-none"
        label="Minimum Points Threshold"
        helpText="Holders below this threshold won't be eligible"
      >
        <TextField.Input
          type="number"
          min="0"
          value={minThreshold}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinThreshold(e.target.value)}
        />
      </TextField>

      {/* Rate mode */}
      <div className="flex flex-col gap-2">
        <span className="text-caption-bold font-caption-bold text-default-font">
          Rate Mode (optional - can set after closing)
        </span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="rateMode"
              value="per_point"
              checked={rateMode === 'per_point'}
              onChange={() => setRateMode('per_point')}
            />
            <span className="text-body font-body text-default-font">Per Point</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="rateMode"
              value="fixed_pool"
              checked={rateMode === 'fixed_pool'}
              onChange={() => setRateMode('fixed_pool')}
            />
            <span className="text-body font-body text-default-font">Fixed Pool</span>
          </label>
        </div>

        {rateMode === 'per_point' && (
          <TextField className="h-auto w-full max-w-[300px] flex-none" label="Rate Per Point">
            <TextField.Input
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g., 0.5"
              value={ratePerPoint}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRatePerPoint(e.target.value)}
            />
          </TextField>
        )}

        {rateMode === 'fixed_pool' && (
          <TextField className="h-auto w-full max-w-[300px] flex-none" label="Total Pool Amount">
            <TextField.Input
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g., 10000"
              value={totalPool}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotalPool(e.target.value)}
            />
          </TextField>
        )}
      </div>

      {/* Custom message */}
      <TextArea className="h-auto w-full flex-none" label="Custom Message (optional)" helpText="Shown to holders in the claim banner">
        <TextArea.Input
          placeholder="E.g., Monthly rewards for February 2026"
          value={customMessage}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCustomMessage(e.target.value)}
        />
      </TextArea>

      {openMutation.error && (
        <div className="text-caption font-caption text-red-600">
          {openMutation.error.message}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          type="submit"
          variant="brand-primary"
          disabled={openMutation.isPending || selectedCollections.length === 0}
        >
          {openMutation.isPending ? 'Opening...' : 'Open Period'}
        </Button>
        <Button variant="neutral-secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ---- PeriodManagement ----

interface PeriodManagementProps {
  projectSlug: string;
  period: ClaimPeriod;
  projectCollections: Array<{ id: string; name: string }>;
}

const PeriodManagement: React.FC<PeriodManagementProps> = ({
  projectSlug,
  period,
  projectCollections,
}) => {
  const closeMutation = useClosePeriod(projectSlug);
  const setRateMutation = useSetRate(projectSlug);
  const distributeMutation = useMarkDistributed(projectSlug);
  const exportMutation = useExportCSV(projectSlug);
  const { data: claimantsData } = useClaimants(projectSlug, period.id);

  const [rateMode, setRateMode] = useState<'per_point' | 'fixed_pool'>(
    (period.rate_mode as 'per_point' | 'fixed_pool') || 'per_point'
  );
  const [ratePerPoint, setRatePerPoint] = useState(period.rate_per_point != null ? String(Number(period.rate_per_point)) : '');
  const [totalPool, setTotalPool] = useState(period.total_pool != null ? String(Number(period.total_pool)) : '');
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDistribute, setConfirmDistribute] = useState(false);

  const eligibleNames = period.eligible_collection_ids
    .map(id => projectCollections.find(c => c.id === id)?.name || id)
    .join(', ');

  const handleSetRate = () => {
    const data: any = { periodId: period.id, rate_mode: rateMode };
    if (rateMode === 'per_point' && ratePerPoint) {
      data.rate_per_point = parseFloat(ratePerPoint);
    }
    if (rateMode === 'fixed_pool' && totalPool) {
      data.total_pool = parseFloat(totalPool);
    }
    setRateMutation.mutate(data);
  };

  return (
    <div className="flex flex-col gap-4 rounded border border-solid border-neutral-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            Current Period
          </span>
          <Badge variant={period.status === 'open' ? 'success' : period.status === 'closed' ? 'warning' : 'neutral'}>
            {period.status}
          </Badge>
        </div>
        <span className="text-caption font-caption text-subtext-color">
          Opened {new Date(period.opened_at).toLocaleDateString()}
        </span>
      </div>

      <div className="text-caption font-caption text-subtext-color">
        Eligible: {eligibleNames}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-caption font-caption text-subtext-color">
        <span>Reward type: {period.required_reward_address_type === 'coreum' ? 'Coreum' : 'XRP'}</span>
        <span className="text-neutral-400">|</span>
        {period.rate_mode === 'per_point' ? (
          <span>Per point: {period.rate_per_point != null ? Number(period.rate_per_point).toLocaleString() : 'not set'}</span>
        ) : (
          <span>Fixed pool: {period.total_pool != null ? Number(period.total_pool).toLocaleString() : 'not set'}</span>
        )}
      </div>

      {/* Stats + Actions for open periods */}
      {period.status === 'open' && (
        <>
          <ClaimStats
            claimCount={period.live_claim_count ?? period.total_claims ?? 0}
            eligibleWallets={claimantsData?.eligible_stats?.total_wallets ?? null}
            claimedPoints={claimantsData ? claimantsData.claims.reduce((sum, c) => sum + (c.live_points ?? 0), 0) : null}
            totalPoints={claimantsData?.eligible_stats?.total_points ?? null}
          />
          <div className="flex items-center justify-end gap-3">
            <Button
              className="w-fit"
              variant="neutral-secondary"
              icon={<FeatherDownload />}
              disabled={exportMutation.isPending}
              onClick={() => exportMutation.mutate(period.id)}
            >
              {exportMutation.isPending ? 'Downloading...' : 'Download CSV'}
            </Button>
            <Button className="w-fit" variant="neutral-secondary" onClick={() => setConfirmClose(true)}>
              Close Claim Period
            </Button>
          </div>
          {confirmClose && (
            <div className="flex items-center gap-3 p-3 rounded border border-solid border-yellow-600/30 bg-yellow-600/10">
              <FeatherAlertCircle className="h-4 w-4 text-yellow-500" />
              <span className="text-caption font-caption text-default-font">
                This will close the claim window, snapshot all points, and prevent new claims.
              </span>
              <Button
                variant="destructive-primary"
                size="small"
                disabled={closeMutation.isPending}
                onClick={() => closeMutation.mutate(period.id)}
              >
                {closeMutation.isPending ? 'Closing...' : 'Confirm Close'}
              </Button>
              <Button variant="neutral-secondary" size="small" onClick={() => setConfirmClose(false)}>
                Cancel
              </Button>
            </div>
          )}
        </>
      )}

      {/* Stats for closed/distributed periods */}
      {period.status !== 'open' && period.total_claims !== null && (
        <div className="flex flex-col gap-3">
          <ClaimStats
            claimCount={period.total_claims ?? 0}
            eligibleWallets={period.total_eligible_wallets}
            claimedPoints={period.total_points_claimants}
            totalPoints={period.total_points_all_holders}
          />
        </div>
      )}

      {period.status === 'closed' && (
        <div className="flex flex-col gap-4">
          {/* Rate configuration */}
          <div className="flex flex-col gap-2 p-3 bg-default-background rounded border border-solid border-neutral-border">
            <span className="text-caption-bold font-caption-bold text-default-font">
              Payout Rate
            </span>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="closedRateMode"
                  checked={rateMode === 'per_point'}
                  onChange={() => setRateMode('per_point')}
                />
                <span className="text-body font-body text-default-font">Per Point</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="closedRateMode"
                  checked={rateMode === 'fixed_pool'}
                  onChange={() => setRateMode('fixed_pool')}
                />
                <span className="text-body font-body text-default-font">Fixed Pool</span>
              </label>
            </div>
            <div className="flex items-end gap-3">
              {rateMode === 'per_point' ? (
                <TextField className="h-auto w-full max-w-[200px] flex-none" label="Rate Per Point">
                  <TextField.Input
                    type="number"
                    step="0.01"
                    value={ratePerPoint}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRatePerPoint(e.target.value)}
                  />
                </TextField>
              ) : (
                <TextField className="h-auto w-full max-w-[200px] flex-none" label="Total Pool">
                  <TextField.Input
                    type="number"
                    step="0.01"
                    value={totalPool}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotalPool(e.target.value)}
                  />
                </TextField>
              )}
              <Button
                variant="neutral-secondary"
                size="small"
                disabled={setRateMutation.isPending}
                onClick={handleSetRate}
              >
                {setRateMutation.isPending ? 'Updating...' : 'Update Rate'}
              </Button>
            </div>
            {setRateMutation.error && (
              <span className="text-caption font-caption text-red-600">{setRateMutation.error.message}</span>
            )}
          </div>

          {/* Export CSV */}
          <div className="flex justify-end">
            <Button
              variant="neutral-secondary"
              icon={<FeatherDownload />}
              disabled={exportMutation.isPending}
              onClick={() => exportMutation.mutate(period.id)}
            >
              {exportMutation.isPending ? 'Downloading...' : 'Download CSV'}
            </Button>
          </div>
        </div>
      )}

      {period.status === 'distributed' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FeatherCheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-body font-body text-green-700">
              Rewards distributed on {period.distributed_at ? new Date(period.distributed_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <Button
            variant="neutral-secondary"
            size="small"
            icon={<FeatherDownload />}
            disabled={exportMutation.isPending}
            onClick={() => exportMutation.mutate(period.id)}
          >
            Export CSV
          </Button>
        </div>
      )}

      {closeMutation.error && (
        <span className="text-caption font-caption text-red-600">{closeMutation.error.message}</span>
      )}
      {distributeMutation.error && (
        <span className="text-caption font-caption text-red-600">{distributeMutation.error.message}</span>
      )}

      {/* Claimants table */}
      <PeriodClaimantsTable projectSlug={projectSlug} periodId={period.id} periodStatus={period.status} />

      {/* Mark as Distributed - below table, centered */}
      {period.status === 'closed' && (
        <div className="flex justify-center mt-2">
          {confirmDistribute ? (
            <div className="flex items-center gap-2">
              <span className="text-caption font-caption text-subtext-color">
                Mark as distributed?
              </span>
              <Button
                variant="brand-primary"
                size="small"
                disabled={distributeMutation.isPending}
                onClick={() => distributeMutation.mutate(period.id)}
              >
                {distributeMutation.isPending ? 'Marking...' : 'Confirm'}
              </Button>
              <Button variant="neutral-secondary" size="small" onClick={() => setConfirmDistribute(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="brand-primary" onClick={() => setConfirmDistribute(true)}>
              Mark as Distributed
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ---- ClaimStats ----

const ClaimStats: React.FC<{
  claimCount: number;
  eligibleWallets: number | null;
  claimedPoints: number | null;
  totalPoints: number | null;
}> = ({ claimCount, eligibleWallets, claimedPoints, totalPoints }) => {
  const walletPct = eligibleWallets && eligibleWallets > 0
    ? ((claimCount / eligibleWallets) * 100).toFixed(1)
    : null;
  const pointsPct = totalPoints && totalPoints > 0 && claimedPoints != null
    ? ((claimedPoints / totalPoints) * 100).toFixed(1)
    : null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-1">
        <span className="text-body font-body text-default-font">
          {claimCount.toLocaleString()}
        </span>
        {eligibleWallets != null ? (
          <span className="text-caption font-caption text-subtext-color">
            / {eligibleWallets.toLocaleString()} eligible wallets claimed ({walletPct}%)
          </span>
        ) : (
          <span className="text-caption font-caption text-subtext-color">Claims</span>
        )}
      </div>
      {claimedPoints != null && totalPoints != null && totalPoints > 0 && (
        <span className="text-caption font-caption text-subtext-color">
          {Number(claimedPoints).toLocaleString()} / {Number(totalPoints).toLocaleString()} eligible points claimed ({pointsPct}%)
        </span>
      )}
    </div>
  );
};

// ---- CopyableAddress ----

const CopyableAddress: React.FC<{
  address: string;
  href?: string;
  truncateStart?: number;
  truncateEnd?: number;
}> = ({ address, href, truncateStart = 6, truncateEnd = 4 }) => {
  const [copied, setCopied] = useState(false);
  const truncated = `${address.slice(0, truncateStart)}...${address.slice(-truncateEnd)}`;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // ignore
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-600 hover:text-brand-700 hover:underline"
        >
          {truncated}
        </a>
      ) : (
        <span>{truncated}</span>
      )}
      <button
        onClick={handleCopy}
        className="p-0.5 rounded hover:bg-neutral-100 transition-colors"
        title={copied ? 'Copied!' : 'Copy address'}
      >
        {copied ? (
          <FeatherCheck className="w-3.5 h-3.5 text-green-500" />
        ) : (
          <FeatherCopy className="w-3.5 h-3.5 text-subtext-color hover:text-default-font" />
        )}
      </button>
    </span>
  );
};

// ---- PeriodClaimantsTable ----

interface PeriodClaimantsTableProps {
  projectSlug: string;
  periodId: string;
  periodStatus?: string;
}

const PAGE_SIZE = 10;

const PeriodClaimantsTable: React.FC<PeriodClaimantsTableProps> = ({
  projectSlug,
  periodId,
  periodStatus,
}) => {
  const { data, isLoading } = useClaimants(projectSlug, periodId);
  const isOpen = periodStatus === 'open';
  const [page, setPage] = useState(0);

  if (isLoading) {
    return <div className="text-subtext-color py-2">Loading claimants...</div>;
  }

  if (!data || data.claims.length === 0) {
    return <div className="text-subtext-color py-2">No claimants for this period.</div>;
  }

  // Calculate estimated payouts for open periods
  const hasLivePoints = isOpen && data.claims.some(c => c.live_points != null);
  const periodRate = data.period;
  const totalLivePoints = hasLivePoints
    ? data.claims.reduce((sum, c) => sum + (c.live_points ?? 0), 0)
    : 0;

  const getEstimatedPayout = (points: number): number | null => {
    if (!periodRate || points === 0) return null;
    if (periodRate.rate_mode === 'per_point' && periodRate.rate_per_point) {
      return points * periodRate.rate_per_point;
    }
    if (periodRate.rate_mode === 'fixed_pool' && periodRate.total_pool && totalLivePoints > 0) {
      return (points / totalLivePoints) * periodRate.total_pool;
    }
    return null;
  };

  const hasEstPayout = hasLivePoints && (
    (periodRate?.rate_mode === 'per_point' && periodRate?.rate_per_point) ||
    (periodRate?.rate_mode === 'fixed_pool' && periodRate?.total_pool)
  );

  // Show reward address column when reward type is not XRP (e.g. Coreum)
  const showRewardAddr = periodRate?.required_reward_address_type
    ? periodRate.required_reward_address_type !== 'xrp'
    : !isOpen; // fallback: always show for closed periods
  const rewardAddrLabel = periodRate?.required_reward_address_type === 'coreum'
    ? 'Coreum Address'
    : 'Reward Address';

  // Client-side pagination
  const totalPages = Math.ceil(data.claims.length / PAGE_SIZE);
  const paginatedClaims = data.claims.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="flex flex-col gap-2 mt-2">
      <span className="text-caption-bold font-caption-bold text-subtext-color">
        Claimants ({data.total})
      </span>
      <div className="overflow-x-auto">
        <table className="w-full text-body font-body">
          <thead>
            <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
              {!isOpen && <th className="px-2 py-1">Rank</th>}
              <th className="px-2 py-1">XRPL Address</th>
              {showRewardAddr && <th className="px-2 py-1">{rewardAddrLabel}</th>}
              <th className="px-2 py-1">Claimed</th>
              <th className="px-2 py-1 text-right">Points</th>
              <th className="px-2 py-1 text-right">
                {isOpen ? (hasEstPayout ? 'Est. Payout' : '') : 'Payout'}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedClaims.map(claim => {
              const points = isOpen ? (claim.live_points ?? 0) : claim.snapshotted_points;
              const payout = isOpen
                ? getEstimatedPayout(claim.live_points ?? 0)
                : claim.calculated_payout;

              return (
                <tr key={claim.id} className="border-b border-neutral-border">
                  {!isOpen && <td className="px-2 py-1 text-default-font">{claim.rank ?? '-'}</td>}
                  <td className="px-2 py-1 text-caption font-mono">
                    <CopyableAddress
                      address={claim.xrpl_address}
                      href={`/wallets/${claim.xrpl_address}`}
                    />
                  </td>
                  {showRewardAddr && (
                    <td className="px-2 py-1 text-caption font-mono text-default-font">
                      {claim.reward_address ? (
                        <CopyableAddress address={claim.reward_address} truncateStart={8} />
                      ) : (
                        <span className="text-subtext-color">-</span>
                      )}
                    </td>
                  )}
                  <td className="px-2 py-1 text-caption text-default-font">
                    {new Date(claim.claimed_at).toLocaleString()}
                  </td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {points != null ? Number(points).toLocaleString() : '-'}
                  </td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {payout != null ? Number(payout).toLocaleString(undefined, { maximumFractionDigits: 2 }) : (hasEstPayout || !isOpen) ? '-' : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-1">
          <button
            className="text-caption font-caption text-brand-600 hover:text-brand-700 disabled:text-neutral-400 disabled:cursor-default"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          <span className="text-caption font-caption text-subtext-color">
            {page + 1} / {totalPages}
          </span>
          <button
            className="text-caption font-caption text-brand-600 hover:text-brand-700 disabled:text-neutral-400 disabled:cursor-default"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
